import todo from '../images/security/todo.jpg';
import doors from '../images/security/doors.jpg';
import windows from '../images/security/windows.jpg';
import rooms from '../images/security/rooms.jpg';
import hallways from '../images/security/hallways.jpg';
import remotedoors from '../images/security/remotedoors.jpg';
import entryway from '../images/security/entryway.jpg';
import nonentry from '../images/security/nonentry.jpg';

const numDoors = {
    group: 'security',
    name: 'numDoors',
    title: 'Doors',
    question: 'How many doors lead into your home?',
    shorthand: 'doors to secure',
    description: 'In order to secure your home, you\'ll want to monitor the exterior doors that lead into and out of your home.',
    image: doors,
    type: 'numeric',
    optionsText: 'Select number of doors',
    onSelect: (selection) => selection,
};

const numWindows = {
    group: 'security',
    name: 'numWindows',
    title: 'Windows',
    question: 'How many windows need to be secured?',
    shorthand: 'windows to secure',
    description: 'In order to secure your home, you\'ll want to monitor the exterior windows that a burglar could use to break into your home.',
    image: windows,
    type: 'numeric',
    optionsText: 'Select number of windows',
    onSelect: (selection) => selection,
};

const numRooms = {
    group: 'security',
    name: 'numRooms',
    title: 'Rooms',
    question: 'How many rooms do you want to monitor?',
    shorthand: 'rooms to monitor',
    description: 'In order to secure your home, you\'ll want to monitor each room to watch for movement when you\'re out, or even just keep an eye on the babysitter.',
    image: rooms,
    type: 'numeric',
    optionsText: 'Select number of rooms',
    onSelect: (selection) => selection,
};

const numOutdoorEntry = {
    group: 'security',
    name: 'numOutdoorEntry',
    title: 'Outdoor Cameras',
    question: 'How many entryways do you want to monitor?',
    shorthand: 'entryways to monitor',
    description: 'In order to secure your home, you\'ll want to monitor outside the entryways of your home. If a package arrives, an uninvited guests knocks, or a package disappears off your front porch, you\'ll have digital proof.',
    image: entryway,
    type: 'numeric',
    optionsText: 'Select number of entryways',
    onSelect: (selection) => selection,
};

const numOutdoorSpace = {
    group: 'security',
    name: 'numOutdoorSpace',
    title: 'Outdoor Cameras',
    question: 'How many non-entryway areas do you want to monitor?',
    shorthand: 'non-entryway areas to monitor',
    description: 'In order to secure your home, you\'ll want to monitor around your home, such as the side of your house or your backyard, to deter uninvited guests on your property.',
    image: nonentry,
    type: 'numeric',
    optionsText: 'Select number of non-entryways',
    onSelect: (selection) => selection,
};

const numHallways = {
    group: 'security',
    name: 'numHallways',
    title: 'Hallways',
    question: 'How many hallways and passageways do you want to monitor?',
    shorthand: 'hallways to monitor',
    description: 'In order to secure your home, you\'ll want to monitor each hallway/passageway to watch for movement when you\'re out, or even just keep an eye on the babysitter.',
    image: hallways,
    type: 'numeric',
    optionsText: 'Select number of hallways',
    onSelect: (selection) => selection,
};

const numDoorLocks = {
    group: 'security',
    name: 'numDoorLocks',
    title: 'Doors',
    question: 'How many doors do you want to control remotely?',
    shorthand: 'doors controlled remotely',
    description: 'Smart remote door locks will allow you to control access to your home, whether it\'s a child coming home from school, a housekeeper coming to clean, or an AirBnB guest you\'d like to let in (or keep out)',
    image: remotedoors,
    type: 'numeric',
    optionsText: 'Select number of doors',
    onSelect: (selection) => selection,
};

const OBJECTIVES = {
    ENTER_HOME: 'Notified on entering home',
    VOICE_CONTROL: 'Voice control',
    MONITOR_INSIDE: 'Monitor inside home',
    MONITOR_OUTSIDE: 'Monitor outside home',
    MOTION: 'Notified on motion',
    DETERRENCE: 'Deter intruders',
    BREAKIN: 'Notified of break-in',
    ACCESS: 'Control access',
};

const start = {
    group: 'security',
    name: 'start',
    question: 'What is your primary security objective?',
    description: 'Home security is important to protect your family, your belongings, and your home. There are many different ways to secure your home; select which ones you\'d like to do.',
    image: todo,
    type: 'multiselect',
    optionsText: 'Select features',
    options: () => [
        {
            label: 'Home entry notifications',
            value: OBJECTIVES.ENTER_HOME,
            queue: [numDoors, numWindows],
        },
        {
            label: 'Voice-controlled security',
            value: OBJECTIVES.VOICE_CONTROL,
        },
        {
            label: 'Indoor video monitoring',
            value: OBJECTIVES.MONITOR_INSIDE,
            queue: [numRooms],
        },
        {
            label: 'Outdoor video monitoring',
            value: OBJECTIVES.MONITOR_OUTSIDE,
            queue: [numOutdoorEntry, numOutdoorSpace],
        },
        {
            label: 'Home motion notifications',
            value: OBJECTIVES.MOTION,
            queue: [numHallways],
        },
        {
            label: 'Deter intruders with alarms',
            value: OBJECTIVES.DETERRENCE,
            queue: [numDoors, numOutdoorEntry, numOutdoorSpace],
        },
        {
            label: 'Home break-in notifications',
            value: OBJECTIVES.BREAKIN,
        },
        {
            label: 'Remote key access & sharing',
            value: OBJECTIVES.ACCESS,
            queue: [numDoorLocks],
        },
    ],
    onSelect: (selection, questions) => {
        selection.forEach((option) => {
            if(!option.queue)
                return;

            option.queue.forEach((question) => {
                if(questions.indexOf(question) === -1)
                    questions.push(question);
            });
        });

        return selection.map(({value}) => value);
    },
    onBack(selection, questions) {
        if(!selection)
            return;

        selection.forEach((option) => {
            const optionObj = this.options().find((opt) => opt.value === option);
            if(!optionObj.queue)
                return;

            optionObj.queue.forEach((question) => {
                const pos = questions.indexOf(question);
                if(pos > -1)
                    questions.splice(pos, 1);
            });
        });
    },
};

const recommendations = [
    {
        name: 'Echo Dot (3rd Gen), Charcoal',
        asin: 'B0792KTHKJ',
        group: 'Bases',
        recommended: true,

        // Remove once API is integrated
        price: 49.99,
        image: 'https://images-na.ssl-images-amazon.com/images/I/6182S7MYC2L._SL1000_.jpg',

        validate: (results) => (
            results.security.start.indexOf(OBJECTIVES.VOICE_CONTROL) > -1
            || results.security.start.indexOf(OBJECTIVES.BREAKIN) > -1
        ) && !(
            results.security.start.indexOf(OBJECTIVES.MONITOR_OUTSIDE) > -1
            || results.security.start.indexOf(OBJECTIVES.DETERRENCE) > -1
        ),
        quantity: () => 0,
    },
    {
        name: 'Echo Show (2nd Gen)',
        asin: 'B077SXWSRP',
        group: 'Bases',
        recommended: true,

        // Remove once API is integrated
        price: 229.99,
        image: 'https://images-na.ssl-images-amazon.com/images/I/51-4Au4WeML._SL1000_.jpg',

        validate: (results) => results.security.start.indexOf(OBJECTIVES.MONITOR_OUTSIDE) > -1
                || results.security.start.indexOf(OBJECTIVES.DETERRENCE) > -1,
        quantity: () => 0,
    },
    {
        name: 'Ring Contact Sensor 2 Pack Alarm 2, White',
        asin: 'B07DNZZCPX',
        group: 'Alarms',

        // Remove once API is integrated
        price: 39.99,
        image: 'https://images-na.ssl-images-amazon.com/images/I/71xszCJja%2BL._SL1500_.jpg',

        validate: (results) => results.security.start.indexOf(OBJECTIVES.ENTER_HOME) > -1
            || results.security.start.indexOf(OBJECTIVES.DETERRENCE) > -1,
        quantity: (results) => {
            const total = results.security.numDoors + results.security.numWindows - 1;

            return Math.ceil(total / 2);
        },
    },
    {
        name: 'Ring Alarm Home Security System',
        asin: 'B07D7Q6CHB',
        group: 'Motion Trackers',

        // Remove once API is integrated
        price: 199,
        image: 'https://images-na.ssl-images-amazon.com/images/I/71wCBizPK8L._SL1500_.jpg',

        validate: (results) => results.security.start.indexOf(OBJECTIVES.ENTER_HOME) > -1
            || results.security.start.indexOf(OBJECTIVES.VOICE_CONTROL) > -1
            || results.security.start.indexOf(OBJECTIVES.MOTION) > -1
            || results.security.start.indexOf(OBJECTIVES.DETERRENCE) > -1,
        quantity: () => 0,
    },
    {
        name: 'Wyze Cam 1080p HD Indoor Wireless Smart Home Camera with Night Vision, 2-Way Audio',
        asin: 'B076H3SRXG',
        group: 'Motion Trackers',

        // Remove once API is integrated
        price: 25.98,
        image: 'https://images-na.ssl-images-amazon.com/images/I/51H5U1Q8RRL._SL1234_.jpg',

        validate: (results) => results.security.start.indexOf(OBJECTIVES.MONITOR_INSIDE) > -1,
        quantity: (results) => results.security.numRooms,
    },
    {
        name: 'Ring Spotlight Cam Battery HD Security Camera with Built Two-Way Talk and a Siren Alarm, White',
        asin: 'B0758L64L9',
        group: 'Motion Trackers',

        // Remove once API is integrated
        price: 199,
        image: 'https://images-na.ssl-images-amazon.com/images/I/51YtTAuAqRL._SL1000_.jpg',

        validate: (results) => results.security.start.indexOf(OBJECTIVES.MONITOR_OUTSIDE) > -1
            && results.security.start.indexOf('alarm') === -1,
        quantity: (results) => results.security.numOutdoorSpace,
    },
    {
        name: 'Ring Wi-Fi Enabled Video Doorbell in Satin Nickel',
        asin: 'B00N2ZDXW2',
        group: 'Motion Trackers',

        // Remove once API is integrated
        price: 99.99,
        image: 'https://images-na.ssl-images-amazon.com/images/I/51D1asikWnL._SL1000_.jpg',

        validate: (results) => results.security.start.indexOf(OBJECTIVES.MONITOR_OUTSIDE) > -1,
        quantity: (results) => results.security.numOutdoorEntry,
    },
    {
        name: 'Ring Alarm Motion Detector, White',
        asin: 'B07DNSZPXG',
        group: 'Motion Trackers',

        // Remove once API is integrated
        price: 29.99,
        image: 'https://images-na.ssl-images-amazon.com/images/I/61Jfgkc1yTL._SL1500_.jpg',

        validate: (results) => results.security.start.indexOf(OBJECTIVES.MOTION) > -1,
        quantity: (results) => {
            const total = results.security.numHallways - 1;
            if(total <= 0 || total % 2 === 0)
                return -1;

            return total;
        },
    },
    {
        name: 'Ring Motion Detector 2 Pack Alarm 2, White',
        asin: 'B07DNRVG48',
        group: 'Motion Trackers',

        // Remove once API is integrated
        price: 59.98,
        image: 'https://images-na.ssl-images-amazon.com/images/I/71SXUKST2eL._SL1500_.jpg',

        validate: (results) => results.security.start.indexOf(OBJECTIVES.MOTION) > -1,
        quantity: (results) => {
            const total = results.security.numHallways - 1;
            if(total <= 0 || total % 2 === 1)
                return -1;

            return total / 2;
        },
    },
    {
        name: 'Ring Floodlight Camera Motion-Activated HD Security Cam Two-Way Talk and Siren Alarm, White',
        asin: 'B0727XJQLD',
        group: 'Motion Trackers',

        // Remove once API is integrated
        price: 249,
        image: 'https://images-na.ssl-images-amazon.com/images/I/51szPIzkLXL._SL1000_.jpg',

        validate: (results) => results.security.start.indexOf(OBJECTIVES.DETERRENCE) > -1,
        quantity: (results) => results.security.numOutdoorSpace,
    },
    {
        name: 'August Smart Lock, 3rd Gen technology - Silver',
        asin: 'B0752XNC8M',
        group: 'Locks',

        // Remove once API is integrated
        price: 129.77,
        image: 'https://images-na.ssl-images-amazon.com/images/I/51erJ1k7xpL._SL1000_.jpg',

        validate: (results) => results.security.start.indexOf(OBJECTIVES.ACCESS) > -1,
        quantity: (results) => results.security.numDoorLocks,
    },
];

export default {
    start,
    numDoors,
    numWindows,
    numDoorLocks,
    numHallways,
    numOutdoorEntry,
    numOutdoorSpace,
    numRooms,
    recommendations,
};
